import type { FC, ReactNode } from 'react'
import type { ClientLogo, Tone } from '@/types'

import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { EASE_OUT_EXPO, MaskLines } from '@/components/motion/reveal'
import { SignalField } from '@/components/motion/signal-field'
import { ProximityText } from '@/components/motion/proximity-text'
import { ScrambleText } from '@/components/motion/scramble-text'
import { ToneSection } from '@/components/generals/tone'
import { Cta } from '@/components/generals/cta'
import { ClientStrip } from '@/components/landing/shared/client-strip'

interface IndustryHeroProps {
    tone: Tone
    accent: string
    index: string
    name: string
    word: string
    lines: ReactNode[]
    intro: string
    clients: ClientLogo[]
    visual?: ReactNode
}

// Shared opening for the industry pages: a field of transactions tinted in the
// industry's colour, the statement up top, and the industry's name set across
// the full width, stretching under the cursor.
export const IndustryHero: FC<IndustryHeroProps> = ({ tone, accent, index, name, word, lines, intro, clients, visual }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduce = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
    const wordX = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['0%', '-14%'])
    const wordY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['0%', '-35%'])
    const contentY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['0%', '22%'])
    const fieldScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1, 1.2])
    const wordSize = `${Math.min(24, 150 / Math.max(4, word.length))}vw`

    return (
        <ToneSection tone={tone} id='hero' className='overflow-hidden'>
            <div ref={ref} className='relative flex min-h-svh flex-col overflow-hidden px-5 pb-6 pt-28 md:px-8 lg:px-12 lg:pt-32'>
                <motion.div
                    className='absolute inset-0 [mask-image:radial-gradient(ellipse_80%_75%_at_65%_40%,black_25%,transparent_100%)]'
                    style={{ scale: fieldScale }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.6, delay: 0.2 }}
                >
                    <SignalField accent={accent} />
                </motion.div>

                <div aria-hidden className='pointer-events-none absolute -right-48 -top-24 size-[40rem] rounded-full opacity-25 blur-[150px]' style={{ backgroundColor: accent }} />

                <motion.div className='relative z-10' style={{ y: contentY }}>
                    <div className='flex items-center justify-between gap-6 t-label text-tone-muted'>
                        <ScrambleText text={`${index} / 03 · Industries`} delay={200} />
                        <span className='flex items-center gap-2'>
                            <span className='size-1.5 rounded-full' style={{ backgroundColor: accent }} />
                            <ScrambleText text={name} delay={350} />
                        </span>
                    </div>

                    <div className='mt-14 grid gap-12 lg:mt-20 lg:grid-cols-12'>
                        <div className='flex flex-col gap-10 lg:col-span-7'>
                            <MaskLines
                                as='h1'
                                immediate
                                delay={0.15}
                                className='font-heading text-[clamp(2.5rem,5.2vw,5.5rem)] font-semibold uppercase leading-[0.9] tracking-[-0.035em] wdth-75'
                                lines={lines}
                            />

                            <motion.div
                                className='flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between lg:flex-col lg:items-start'
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1.1, ease: EASE_OUT_EXPO, delay: 0.6 }}
                            >
                                <p className='t-lead max-w-lg text-tone-muted'>
                                    {intro}
                                </p>
                                <Cta href='/contact' cursor='Book'>
                                    Book a call
                                </Cta>
                            </motion.div>
                        </div>

                        {visual && (
                            <motion.div
                                className='flex items-center justify-center lg:col-span-4 lg:col-start-9 lg:justify-end'
                                initial={{ opacity: 0, y: 40, filter: 'blur(12px)' }}
                                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                transition={{ duration: 1.3, ease: EASE_OUT_EXPO, delay: 0.5 }}
                            >
                                {visual}
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                <motion.div
                    aria-hidden
                    className='relative z-10 mt-auto overflow-hidden pt-16'
                    style={{ x: wordX, y: wordY }}
                >
                    <motion.div
                        className='font-heading uppercase leading-[0.78] tracking-[-0.05em]'
                        style={{ fontSize: wordSize, color: accent }}
                        initial={{ y: '100%' }}
                        animate={{ y: '0%' }}
                        transition={{ duration: 1.4, ease: EASE_OUT_EXPO, delay: 0.35 }}
                    >
                        <ProximityText text={word} minWidth={75} maxWidth={125} minWeight={700} maxWeight={900} radius={320} />
                    </motion.div>
                </motion.div>
            </div>

            <ClientStrip clients={clients} label={`${name} clients`} />
        </ToneSection>
    )
}
